import { Helmet } from "react-helmet-async";
import { usePathname } from "@/lib/navigation";
import {
  absoluteUrl,
  defaultOgLocale,
  defaultSiteDescription,
} from "@/lib/seoConfig";

/**
 * Per-route SEO tags. Pass full `title` string as it should appear in the tab.
 * Canonical defaults to current path if `canonicalPath` is omitted.
 */
export default function SeoHead({
  title,
  description = defaultSiteDescription,
  canonicalPath,
  noindex = false,
  ogType = "website",
  ogImage,
  twitterCard = "summary_large_image",
}) {
  const pathname = usePathname() || "/";
  const pathForCanonical = canonicalPath ?? pathname;
  const canonicalHref = /^https?:\/\//i.test(pathForCanonical)
    ? pathForCanonical
    : absoluteUrl(
        pathForCanonical.startsWith("/") ? pathForCanonical : `/${pathForCanonical}`
      );

  const ogImageAbsolute = ogImage
    ? absoluteUrl(ogImage)
    : absoluteUrl("/favicon.ico");

  const robots = noindex ? "noindex, follow" : "index, follow";

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalHref} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="NovelNet" />
      <meta property="og:locale" content={defaultOgLocale} />
      <meta property="og:url" content={canonicalHref} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageAbsolute} />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageAbsolute} />
    </Helmet>
  );
}
